import React, { useEffect } from 'react';

interface TawkToWidgetProps {
  propertyId?: string;
  widgetId?: string;
}

export const TawkToWidget: React.FC<TawkToWidgetProps> = ({
  propertyId = (import.meta as any).env?.VITE_TAWK_PROPERTY_ID || '',
  widgetId = (import.meta as any).env?.VITE_TAWK_WIDGET_ID || 'default'
}) => {
  // Only initialize if a valid, non-placeholder property ID is provided
  const isValidPropertyId = Boolean(
    propertyId &&
    propertyId.trim() !== '' &&
    propertyId !== '5eac77c1203e206707f8b95a' &&
    propertyId !== 'YOUR_PROPERTY_ID'
  );

  useEffect(() => {
    if (!isValidPropertyId) {
      return;
    }

    // Avoid duplicate script injection
    if (document.getElementById('tawk-to-script')) {
      if ((window as any).Tawk_API?.showWidget) {
        (window as any).Tawk_API.showWidget();
      }
      return;
    }

    let isCancelled = false;
    let timerId: any = null;

    const initTawk = () => {
      if (isCancelled || document.getElementById('tawk-to-script') || !isValidPropertyId) return;

      (window as any).Tawk_API = (window as any).Tawk_API || {};
      (window as any).Tawk_LoadStart = new Date();

      const s1 = document.createElement('script');
      const s0 = document.getElementsByTagName('script')[0];
      s1.id = 'tawk-to-script';
      s1.async = true;
      s1.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
      s1.charset = 'UTF-8';
      s1.setAttribute('crossorigin', '*');

      if (s0 && s0.parentNode) {
        s0.parentNode.insertBefore(s1, s0);
      }
    };

    // Load after idle or first user interaction (pointermove, scroll, touchstart)
    const onUserInteraction = () => {
      cleanupEvents();
      initTawk();
    };

    const cleanupEvents = () => {
      window.removeEventListener('scroll', onUserInteraction);
      window.removeEventListener('pointerdown', onUserInteraction);
      window.removeEventListener('touchstart', onUserInteraction);
      if (timerId) clearTimeout(timerId);
    };

    window.addEventListener('scroll', onUserInteraction, { passive: true, once: true });
    window.addEventListener('pointerdown', onUserInteraction, { passive: true, once: true });
    window.addEventListener('touchstart', onUserInteraction, { passive: true, once: true });

    // Fallback: load after 3 seconds if no interaction
    if (typeof (window as any).requestIdleCallback === 'function') {
      (window as any).requestIdleCallback(() => {
        timerId = setTimeout(initTawk, 3000);
      });
    } else {
      timerId = setTimeout(initTawk, 3000);
    }

    return () => {
      isCancelled = true;
      cleanupEvents();
      if ((window as any).Tawk_API?.hideWidget) {
        (window as any).Tawk_API.hideWidget();
      }
    };
  }, [propertyId, widgetId, isValidPropertyId]);

  return null;
};
